import { useFormik } from 'formik'
import React, { useEffect, useState } from 'react'
import { Helmet } from 'react-helmet'
import { useDispatch, useSelector } from 'react-redux'
import * as Yup from 'yup'

import { EuiSpacer } from '@elastic/eui'
import { EuiTabbedContentTab } from '@elastic/eui/src/components/tabs/tabbed_content/tabbed_content'

import { apiPostAd } from 'api/adcritterApi'
import { Ad } from 'api/interfaces'
import { showSuccessToast } from 'app/appSlice'
import { RootState } from 'app/rootReducer'
import ChooseImageFlyout from 'components/chooseImage/ChooseImageFlyout'
import { fetchUploadedImages } from 'components/chooseImage/searchImageSlice'
import { DesktopWeb, MobileInApp, MobileWeb } from 'components/previews'
import history from 'services/HistoryService'

import { ICampaignBuilderWorkflowStep } from '../../../../builders/createCampaign/creativeBuilder/CreativeBuilderWorkflowManager'
import { fetchCampaign } from '../../../campaign/campaignSlice'
import { setAdCompanyName, setImage, resetCreateAd } from './createAdInternetRetailSlice'
import { CreateNativeAdForm } from '../CreateNativeAdForm'

const CreateStep: React.FC<ICampaignBuilderWorkflowStep> = props => {
  const dispatch = useDispatch()
  const createAd = useSelector((state: RootState) => state.createAdInternetRetail)
  const dictionary = useSelector((state: RootState) => state.dictionary)
  const chosenImage = useSelector((state: RootState) => state.searchImage.chosenImage)
  const { currentAccount } = useSelector((state: RootState) => state.app)
  const [imageError, setImageError] = useState<string | null>(null)
  const campaign = useSelector((state: RootState) => state.campaign)

  useEffect(() => {
    if (currentAccount) {
      dispatch(setAdCompanyName(currentAccount.name))
      dispatch(fetchUploadedImages(currentAccount.id))
    }
  }, [currentAccount, dispatch])

  const adSchema = Yup.object().shape({
    headline: Yup.string().required('Please enter a headline').max(50),
    body: Yup.string().required('Please enter the body copy').max(150),
    companyName: Yup.string().required('Please enter your company name').max(35),
    callToAction: Yup.string().required('Please select a call-to-action'),
    destinationUrl: Yup.string()
      .matches(/^(https:\/\/)/, 'Please enter a valid website URL including https:// (ex. https://www.domain.com)')
      .url('Please enter a valid website URL including https:// (ex. https://www.domain.com)')
      .required('Please enter the website URL including https:// (ex. https://www.domain.com)')
      .max(2048)
  })

  const formik = useFormik({
    initialValues: createAd.ad!,
    enableReinitialize: true,
    validationSchema: adSchema,
    onSubmit: (values: Ad) => {
      let errorFound = false
      if (!createAd.image) {
        setImageError('Please select an image')
        errorFound = true
      }

      if (!errorFound) {
        apiPostAd(currentAccount!.id, {
          highQualityUrl: createAd.image!.isPurchased ? '' : createAd.image!.highQualityUrl,
          squareUrl: createAd.image!.isPurchased ? '' : createAd.image!.squareUrl,
          largeSquareUrl: createAd.image!.isPurchased ? '' : createAd.image!.largeSquareUrl,
          thumbnailUrl: createAd.image!.isPurchased ? '' : createAd.image!.thumbnailUrl,
          mobileUrl: createAd.image!.isPurchased ? '' : createAd.image!.mobileUrl,
          imageId: createAd.image!.id,
          isPurchased: createAd.image!.isPurchased,
          headline: values.headline,
          body: values.body,
          companyName: values.companyName,
          callToAction: values.callToAction,
          destinationUrl: values.destinationUrl,
          campaignIds: [campaign.campaign!.id]
        })
          .then(() => {
            formik.setSubmitting(false)
            formik.setStatus(null)
            dispatch(showSuccessToast('Created new ad'))
            dispatch(resetCreateAd())
            dispatch(fetchCampaign(currentAccount!.id, campaign.campaign!.id))
            history.push(`/campaigns/edit/${campaign.campaign!.id}/details`)
          })
          .catch(response => {
            formik.setSubmitting(false)
            formik.setStatus(response.data?.errors.first)
          })
      }
    }
  })

  useEffect(() => {
    if (chosenImage) dispatch(setImage(chosenImage))
  }, [chosenImage, dispatch])

  const previewTabs: Array<EuiTabbedContentTab> = [
    {
      id: 'ex1',
      name: 'Large Example',
      content: <DesktopWeb ad={formik.values} image={createAd.image} />
    },
    {
      id: 'ex2',
      name: 'Medium Example',
      content: <MobileInApp ad={formik.values} image={createAd.image} />
    },
    {
      id: 'ex3',
      name: 'Small Example',
      content: <MobileWeb ad={formik.values} image={createAd.image} />
    }
  ]

  const onBackClick = () => {
    props.onBack?.()
  }

  return (
    <React.Fragment>
      <Helmet>
        <title>Create Ad Builder</title>
      </Helmet>
      <EuiSpacer />
      <CreateNativeAdForm onBack={onBackClick} />
    </React.Fragment>
  )
}

export default CreateStep
